/* eslint-disable camelcase, no-underscore-dangle */
import { useState, useEffect } from 'react';
import { graphql, fetchQuery, useRelayEnvironment } from 'react-relay';
import dynamic from 'next/dynamic';
import { ORImageSpace as Image } from '@/core/image';
import ModalWrapper from '@/helpers/modals/skin2.js';
import { UseZusModalStore } from '@/helpers/modal.js';
import { useTranslation } from '@/helpers/translate';

const queryCarouselLoadMorePictures = graphql`
  query topPicturesLoadMoreQuery($spaceRef: String!) {
    item : getSpaceByRef(ref: $spaceRef) {
      pictures {
        nameId
        extension
        predictName
      }
    }
  }
`;

function SpaceTopPictures({ pictures, spaceRef, title }) {
  const environment = useRelayEnvironment();
  const [currentPictures, setCurrentPictures] = useState(pictures);
  const { t } = useTranslation();

  useEffect(() => {
    if (spaceRef) {
      fetchQuery(environment, queryCarouselLoadMorePictures, {
        spaceRef,
      }, { fetchPolicy: 'store-or-network' }).subscribe({
        next: (dataRes) => {
          setCurrentPictures(dataRes?.item?.pictures);
        },
        error: (error1) => {
          console.log('error1', error1);
        },
      });
    }
  }, []);

  function openCarousel() {
    if (!currentPictures?.length) return null;
    const { setModal1 } = UseZusModalStore.getState();
    document.getElementsByTagName('body')[0].classList.add('overflow-hidden');
    const Carousel = dynamic(import('@/components/space/public/carousel.js'));
    return setModal1(
      <ModalWrapper
        onClose={() => {
          document
            .getElementsByTagName('body')[0]
            .classList.remove('overflow-hidden');
          setModal1(null);
        }}
      >
        <Carousel pictures={currentPictures} />
      </ModalWrapper>,
    );
  }

  const imgClassName = {
    class1: 'col-span-1 cursor-pointer h-[200px] lg:h-[250px] w-full overflow-hidden relative bg-purple-6 cursor-pointer after:bg-black/0 after:transition after:duration-500 after:bg-opacity-0 hover:after:transition after:absolute after:bottom-0 after:right-0 after:top-0 after:left-0 hover:after:bg-black/20',
    class2: 'col-span-1 cursor-pointer h-[200px] lg:h-[250px] w-full overflow-hidden rounded-tr-xl relative bg-purple-6 cursor-pointer after:bg-black/0 after:transition after:duration-500 after:bg-opacity-0 hover:after:transition after:absolute after:bottom-0 after:right-0 after:top-0 after:left-0 hover:after:bg-black/20',
    class3: 'col-span-1 cursor-pointer h-[200px] lg:h-[250px] w-full overflow-hidden relative bg-purple-6 cursor-pointer after:bg-black/0 after:transition after:duration-500 after:bg-opacity-0 hover:after:transition after:absolute after:bottom-0 after:right-0 after:top-0 after:left-0 hover:after:bg-black/20',
    class4: 'col-span-1 cursor-pointer h-[200px] lg:h-[250px] w-full rounded-br-xl overflow-hidden relative bg-purple-6 cursor-pointer after:bg-black/0 after:transition after:duration-500 after:bg-opacity-0 hover:after:transition after:absolute after:bottom-0 after:right-0 after:top-0 after:left-0 hover:after:bg-black/20',
  };

  return (
    <div className="container relative z-10 flex flex-col gap-2 px-0 mx-auto lg:pt-7 md:px-10 lg:px-10 xl:px-20 2xl:px-48 md:flex-row">
      <div className="w-full md:w-1/2 h-[300px] md:h-[408px] lg:h-[508px] relative">
        {!currentPictures?.[0] || (
          <div className="h-[300px] bg-purple-6 md:h-[408px] lg:h-[508px] w-full overflow-hidden rounded-none md:rounded-l-xl md:rounded-r-none relative cursor-pointer after:bg-black/0 after:transition after:duration-500 after:bg-opacity-0 hover:after:transition after:absolute after:bottom-0 after:right-0 after:top-0 after:left-0 hover:after:bg-black/10" onClick={() => openCarousel()}>
            <Image
              picture={currentPictures[0]}
              topPicturesMain
              priority
              showDefault
              options={{
                alt: title,
                index: 0,
              }}
              defaultAlt={`${t('search:filter.space')} ${title} - 0`}
            />
          </div>
        )}
      </div>
      <div className="hidden w-1/2 h-[400px] lg:h-[500px] grid-cols-2 md:grid gap-2">
        {[1, 2, 3, 4].map((imgindex) => {
          if (!currentPictures?.[imgindex]) return null;
          return (
            <div key={`spacepublictoppicturevignettemode${imgindex}`} className={imgClassName[`class${imgindex}`]} onClick={() => openCarousel()}>
              <Image
                picture={currentPictures[imgindex]}
                topPicturesSmall
                showDefault
                options={{
                  alt: title,
                  index: 1,
                }}
                defaultAlt={`${t('search:filter.space')} ${title} - ${imgindex}`}
              />
            </div>
          );
        })}
      </div>
      <button className="absolute flex items-center px-3 py-2 bg-white rounded-lg bottom-4 md:bottom-4 right-4 md:right-4 shadow-button md:mx-10 lg:mx-10 xl:mx-20 2xl:mx-48" onClick={() => openCarousel()}>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="ml-2 font-medium text-[16px]">{t('space:all_pictures')}</span>
        <div className="flex items-center justify-center ml-2 bg-black rounded-xl p-1 w-6 h-6 text-[12px] font-normal text-white"><div>{currentPictures.length}</div></div>
      </button>
    </div>
  );
}

export default SpaceTopPictures;
